import moment from "moment-timezone";
import momentTzNames from "./momentTznames";

export const isDateString = (value: unknown) => {
    return typeof value === "string" && isNaN(Number(value))
};

export const inFormat = (epochOrBlank, format = "DD MMM, YYYY") => {

    if (isDateString(epochOrBlank) && epochOrBlank !== "") {
        return parseStringFormat(epochOrBlank, null, null)?.format(format);
    }
    let val = epochOrBlank === 0 ? 0 : epochOrBlank || undefined;
    return moment(val).format(format)
};

export const getEpoch = (epochOrBlank?, format?: any) => {
    if (isDateString(epochOrBlank) && epochOrBlank !== "") {
        return parseStringFormat(epochOrBlank, null, null)?.valueOf()
    }
    let val = epochOrBlank === 0 ? 0 : epochOrBlank || undefined;
    return moment(val).valueOf();
};

export const getInstance = (epochOrBlank?: any, format?: any, strict?) => {
    if (isDateString(epochOrBlank) && epochOrBlank !== "") {
        return parseStringFormat(epochOrBlank, null, null);
    }
    let val = epochOrBlank === 0 ? 0 : epochOrBlank || undefined;
    return moment(val, format, strict);
};


export const isSame = (date1, date2, unit = "millisecond") => {
    return getInstance(date1 || undefined).isSame(getInstance(date2 || undefined), unit);
};

export const isAfter = (date1, date2, unit = "millisecond") => {
    return getInstance(date1 || undefined).isAfter(getInstance(date2 || undefined), unit);
};

export const isBefore = (date1, date2, unit = "millisecond", inclusive?) => {
    return getInstance(date1 || undefined).isBefore(getInstance(date2 || undefined), unit, inclusive ? "[]" : "()");
};


export const isSameOrAfter = (date1, date2, unit = "millisecond") => {
    return getInstance(date1 || undefined).isSameOrAfter(getInstance(date2 || undefined), unit);
};

export const isSameOrBefore = (date1, date2, unit = "millisecond") => {
    return getInstance(date1 || undefined).isSameOrBefore(getInstance(date2 || undefined), unit);
};

export const isBetween = (currentDate, startDate, endDate, unit = "millisecond", inclusive?) => {
    return getInstance(currentDate || undefined).isBetween(getInstance(startDate || undefined), getInstance(endDate || undefined), unit, inclusive ? "[]" : "()");
};

/**
 * Parses a date string using a single format or multiple formats.
 * @param {string} dateString - The input date string.
 * @param {string | string[]} formatArr - A single format string or an array of formats.
 * @param {boolean} [strict=true] - Whether to enforce strict parsing.
 * @returns {dayjs.Dayjs} A valid or invalid Day.js instance.
 */

const supportedDateTimeFormats = [
    "h:mm A",
    "hh:mm a",
    "h:mm: a",
    "HH:mm",
    "HH:mm:ss",
    "HH:mm:ss a",

    "DD MMM YYYY",
    "D[, ]MMMM[, ]YYYY",
    "DD-MM-YY-HH:mm a",
    "DD-MM-YY-HH:mm A",
    "D/MM/YYYY",
    "DD/MM/YY",
    "DD-MM-YYYY",
    "DD/MM/YYYY",
    "D/M/YYYY",
    "DD/M/YYYY",
    "DD, MMMM, YYYY",
    "DD,MMMM,YYYY",
    "DD.MM.yyyy",
    "YYYY-MM-DD",
    "YYYY/MM/DD",
    "YYYY/M/D",
    "YYYY-M-D",
    "MM/DD/YYYY",
    "MM/DD/YY",
    "MM-DD-YY",
    "M/D/YY",
    "MMM D, YY",
    "MMM D, YYYY",
    "MMM DD, YYYY",
    "DD[-]MM[-]YYYY",
    "DD[, ]MMMM[, ]YYYY",
    "DD-MM-YY",
    "DD[/]MM[/]YYYY",
    "DD, MMM, YYYY",
    "D MMM YYYY",
    "MMMM DD, YYYY",
    "DD MMMM YYYY",
    "DD MMMM YY",
    "DD MMM YY",
    "DD[,]MMMM[,]YYYY",
    "DD,MMMM",
    "DD MMM YYYY hh:mm A",
    "DD MMM YYYY h:mm A",
    "DD[-]M[-]YYYY",
    "ddd MMM DD YYYY"
];


export const parseStringFormat = (dateString, formatArr, strict = true) => {
    if (!dateString) {
        return getInstance(null)
    } else if (typeof dateString === 'string' && dateString?.toLowerCase() === "invalid date") {
        return getInstance(null)
    };
    let a = moment(dateString, supportedDateTimeFormats)
    return a;
};


export const setDefaultTimezone = (timezone?) => {
    moment.updateLocale('en', {
        relativeTime: {
            future: 'in %s',
            past: '%s ago',
            s: '1 sec',
            m: '1 min',
            mm: '%d mins',
            h: '1 hr',
            hh: '%d hrs',
            d: '1 day',
            dd: '%d days',
            M: '1 mon',
            MM: '%d mon',
            y: '1 yr',
            yy: '%d yr',
        }
    });
    if (timezone && momentTzNames?.includes(timezone)) {
        return moment.tz.setDefault(timezone);
    } else {
        return moment.tz.setDefault(moment.tz.guess());
    }
}

export const getInstanceClone = (e) => {
    return moment(e).clone();
}

export const getDuration = (val) => {
    return moment.duration(val);
}

export const addTime = (epochOrMoment, amount, unit) => {
    return getInstanceClone(epochOrMoment || undefined).add(amount, unit);
};

export const subtractTime = (epochOrMoment, amount, unit) => {
    return getInstanceClone(epochOrMoment || undefined).subtract(amount, unit);
};

export const isFutureDate = (date: any) => {
    return isAfter(date, getInstance(), "day");
};

export const DEFAULT_WEEK_START_DAY = "monday";

const DAY_NAME_TO_DOW: Record<string, 0 | 1 | 2 | 3 | 4 | 5 | 6> = {
    sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
};

export const resolveWeekStartDay = (weekStartDay?: string | null): string => {
    const normalized = (weekStartDay ?? "").trim().toLowerCase();
    return normalized in DAY_NAME_TO_DOW ? normalized : DEFAULT_WEEK_START_DAY;
};

export const getWeekStartDow = (weekStartDay?: string | null) =>
    DAY_NAME_TO_DOW[resolveWeekStartDay(weekStartDay)];

export const getStartOfWeek = (epochOrMoment: any, weekStartDay?: string | null) => {
    const dow = getWeekStartDow(weekStartDay);
    const m = getInstanceClone(epochOrMoment);
    return m.subtract((m.day() - dow + 7) % 7, "day").startOf("day");
};

export const getEndOfWeek = (epochOrMoment: any, weekStartDay?: string | null) =>
    getStartOfWeek(epochOrMoment, weekStartDay).add(6, "day").endOf("day");

// Canonical key for a week (epoch ms of week start). Stable across moment instances.
export const getWeekKey = (epochOrMoment: any, weekStartDay?: string | null): number =>
    getStartOfWeek(epochOrMoment, weekStartDay).valueOf();

// Signed integer week distance: weeksBetween(A, B) = round((startOfWeek(B) - startOfWeek(A)) / 1week)
export const weeksBetween = (a: any, b: any, weekStartDay?: string | null): number => {
    const startA = getStartOfWeek(a, weekStartDay);
    const startB = getStartOfWeek(b, weekStartDay);
    return Math.round(startB.diff(startA, "day") / 7);
};

// Window of n consecutive weeks anchored at the week containing `anchor`.
// Returns { start: startOfWeek(anchor), end: endOfWeek(start + (n-1) weeks) }.
export const consecutiveWeeksWindow = (anchor: any, n: number, weekStartDay?: string | null) => {
    const start = getStartOfWeek(anchor, weekStartDay);
    const lastWeekAnchor = getInstanceClone(start).add(n - 1, "week");
    return { start, end: getEndOfWeek(lastWeekAnchor, weekStartDay) };
};

// export default { inFormat, getEpoch, getInstance, isSame, isAfter, isBefore, isSameOrAfter, isSameOrBefore, parseStringFormat, setDefaultTimezone, getDuration };